import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, Checkbox, Input, Textarea } from '@dreamtree-org/twreact-ui';
import { CheckCircle2 } from 'lucide-react';
import { PageHeader, SectionCard } from '@/components';
import { useMutation } from '@/lib/useQuery';
import { isValidPhone } from '../lib/cart';
import { useStorefront } from '../StorefrontContext';

const CHANNELS = [['call', 'Call me'], ['whatsapp', 'WhatsApp me'], ['email', 'Email me']];

/**
 * "Request a quote" (D15): bulk and business enquiries become a lead for the
 * store. The shopper chooses how they may be contacted; nothing else is used.
 */
export default function Quote() {
  const sf = useStorefront();
  const [params] = useSearchParams();
  const listingId = params.get('listing');
  const listingTitle = params.get('title') || '';
  const send = useMutation('sfEnquiry');
  const [f, setF] = useState({
    name: (sf.session && sf.session.name) || '', company: '', phone: (sf.session && sf.session.phone) || '', email: '',
    interest: listingTitle, quantity: '1', budget: '', message: '', consent: { call: true, whatsapp: false, email: false },
  });
  const [touched, setTouched] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const errs = {
    name: !f.name.trim() ? 'Please enter your name' : '',
    phone: f.phone && !isValidPhone(f.phone) ? 'Enter a 10-digit mobile number' : (f.consent.call || f.consent.whatsapp) && !f.phone ? 'Add a number to be called or messaged' : '',
    email: f.consent.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email) ? 'Add an email to be emailed' : '',
    consent: !Object.values(f.consent).some(Boolean) ? 'Choose at least one way we may contact you' : '',
  };
  const err = (k) => (touched && errs[k]) || undefined;

  const submit = async () => {
    setTouched(true);
    if (Object.values(errs).some(Boolean)) return;
    try {
      const r = await send.run({
        name: f.name, company: f.company, phone: f.phone || undefined, email: f.email || undefined, interest: f.interest, listing_id: listingId || undefined,
        quantity: Number(f.quantity) || 1, budget_paise: f.budget ? Math.round(Number(String(f.budget).replace(/[₹,\s]/g, '')) * 100) : undefined, message: f.message, consent: f.consent,
      });
      setDone(r.data);
    } catch {
      /* shown below */
    }
  };

  if (done) {
    return (
      <Card className="mx-auto max-w-lg space-y-2 p-6 text-center">
        <CheckCircle2 className="mx-auto h-10 w-10 text-success-600" aria-hidden="true" />
        <div className="font-semibold">Request {done.id} received</div>
        <p className="text-sm opacity-80">{sf.brand.name} will get back to you with a quotation, only through the channels you chose.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader title="Request a quote" subtitle={`Buying several ${sf.shop.noun} for an office, school or team? Tell us what you need and get a written quotation.`} />
      <SectionCard>
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="Your name" value={f.name} onChange={(e) => set('name', e.target.value)} error={err('name')} required />
          <Input label="Company or institution (optional)" value={f.company} onChange={(e) => set('company', e.target.value)} />
          <Input label="Mobile number" type="tel" inputMode="numeric" value={f.phone} onChange={(e) => set('phone', e.target.value)} error={err('phone')} />
          <Input label="Email" type="email" value={f.email} onChange={(e) => set('email', e.target.value)} error={err('email')} />
          <div className="sm:col-span-2"><Input label="What are you looking for?" value={f.interest} onChange={(e) => set('interest', e.target.value)} placeholder="e.g. 10 × ThinkPad T14, 16 GB, for developers" /></div>
          <Input label="How many?" type="number" min={1} value={f.quantity} onChange={(e) => set('quantity', e.target.value)} />
          <Input label="Total budget (₹, optional)" inputMode="numeric" value={f.budget} onChange={(e) => set('budget', e.target.value)} />
          <div className="sm:col-span-2"><Textarea label="Anything else (optional)" rows={3} maxLength={2000} value={f.message} onChange={(e) => set('message', e.target.value)} /></div>
        </div>
        <fieldset className="space-y-1">
          <legend className="text-sm font-medium">How may {sf.brand.name} contact you about this request?</legend>
          {CHANNELS.map(([k, l]) => (
            <Checkbox key={k} label={l} checked={f.consent[k]} onChange={(e) => set('consent', { ...f.consent, [k]: e.target.checked })} />
          ))}
          {err('consent') ? <p className="text-sm text-error-600">{err('consent')}</p> : null}
          <p className="text-xs opacity-70">Used only for this request. Staff see only the details you agree to share.</p>
        </fieldset>
        {send.error ? <Alert variant="error">{send.error}</Alert> : null}
        <Button loading={send.loading} onClick={submit}>Send request</Button>
      </SectionCard>
    </div>
  );
}
